import type { HeaderDomOverride, HeaderDomOwner, HeaderDomResource, HeaderDomValue } from './interfaces';

/**
 * Adapts a CSS metric to the HypeDS parser without nesting var() expressions.
 *
 * ---
 * 🧠 **Context**
 *
 * - Unwraps a standalone CSS variable so HypeDS can wrap its name once.
 * - Complex expressions containing variables return undefined and need the raw override from {@link getTypographyMetricOverride}.
 *
 * ---
 * @example
 * ```ts
 * const fontSize = getTypographyMetric('var(--font-size-16)');
 * const lineHeight = getTypographyMetric('1.5rem');
 * ```
 *
 * ---
 * @function getTypographyMetric
 * @param {string} value - CSS metric received from theme tokens.
 * @returns {string | undefined} Parser-compatible metric, or undefined when a raw override is needed.
 */
export function getTypographyMetric(value: string) {
	const variable = value.trim().match(/^var\(([\s\S]*)\)$/);

	if (variable) return variable[1];

	return value.includes('--') ? undefined : value;
}

/**
 * Returns a raw CSS expression only when the HypeDS metric parser must be bypassed.
 *
 * ---
 * 🧠 **Context**
 *
 * - Complements {@link getTypographyMetric} for expressions combining variables and calculations.
 * - Simple values are left to the Typography metric props.
 *
 * ---
 * @example
 * ```ts
 * const rawLineHeight = getTypographyMetricOverride('calc(var(--font-size-16) * 1.5)');
 * ```
 *
 * ---
 * @function getTypographyMetricOverride
 * @param {string} value - CSS metric received from theme tokens.
 * @returns {string | undefined} Original complex expression, or undefined when no override is required.
 */
export function getTypographyMetricOverride(value: string) {
	return getTypographyMetric(value) === undefined ? value : undefined;
}

const resources = new WeakMap<Element, Map<string, HeaderDomResource>>();

function sameValue(first: HeaderDomValue, second: HeaderDomValue) {
	return first.value === second.value && first.priority === second.priority;
}

function createOverride(
	element: Element,
	key: string,
	read: () => HeaderDomValue,
	write: (value: HeaderDomValue) => void,
): HeaderDomOverride {
	const owner: HeaderDomOwner = { value: { value: null } };
	let ownedResource: HeaderDomResource | undefined;

	function set(value: string) {
		let elementResources = resources.get(element);

		if (!elementResources) {
			elementResources = new Map();
			resources.set(element, elementResources);
		}

		let resource = elementResources.get(key);
		const current = read();

		if (!resource || !sameValue(current, resource.owners[resource.owners.length - 1].value)) {
			resource = { previous: current, owners: [] };
			elementResources.set(key, resource);
		}

		const position = resource.owners.indexOf(owner);

		if (position !== -1) resource.owners.splice(position, 1);

		owner.value = { value, ...(current.priority === undefined ? {} : { priority: '' }) };
		resource.owners.push(owner);
		ownedResource = resource;

		if (!sameValue(current, owner.value)) write(owner.value);
	}

	function restore() {
		const elementResources = resources.get(element);
		const resource = elementResources?.get(key);

		if (!resource || resource !== ownedResource) return;

		const current = read();

		if (!sameValue(current, resource.owners[resource.owners.length - 1].value)) {
			elementResources?.delete(key);

			return;
		}

		const position = resource.owners.indexOf(owner);

		if (position === -1) return;

		resource.owners.splice(position, 1);
		const next = resource.owners[resource.owners.length - 1]?.value ?? resource.previous;

		if (!sameValue(current, next)) write(next);

		if (!resource.owners.length) elementResources?.delete(key);

		ownedResource = undefined;
	}

	return { set, restore };
}

/**
 * Creates a coordinated temporary override for one inline CSS property.
 *
 * ---
 * 🧠 **Context**
 *
 * - Coordinates only temporary DOM resources written by participating Header instances.
 * - Restore preserves previous values and other active owners, including out-of-order release and CSS priority.
 * - A different external value is preserved; identical writes by non-participating code cannot be distinguished.
 *
 * ---
 * @example
 * ```ts
 * const override = createHeaderStyleOverride(document.documentElement, '--z-index-reference');
 * override.set('100');
 * override.restore();
 * ```
 *
 * ---
 * @function createHeaderStyleOverride
 * @param {HTMLElement} element - DOM element holding the inline property.
 * @param {string} property - CSS property name, including its custom-property prefix when applicable.
 * @returns {HeaderDomOverride} Handle to apply a temporary value and release its ownership.
 */
export function createHeaderStyleOverride(element: HTMLElement, property: string): HeaderDomOverride {
	return createOverride(
		element,
		`style:${property}`,
		() => ({ value: element.style.getPropertyValue(property), priority: element.style.getPropertyPriority(property) }),
		({ value, priority }) => {
			if (value) element.style.setProperty(property, value, priority);
			else element.style.removeProperty(property);
		},
	);
}

/**
 * Creates a coordinated temporary override for one element attribute.
 *
 * ---
 * 🧠 **Context**
 *
 * - Coordinates only temporary DOM resources written by participating Header instances.
 * - Restore preserves previous values and other active owners, including out-of-order release and CSS priority.
 * - A different external value is preserved; identical writes by non-participating code cannot be distinguished.
 *
 * ---
 * @example
 * ```ts
 * const banner = document.querySelector('[data-testid="simulated-banner"]');
 * if (banner) {
 *   const override = createHeaderAttributeOverride(banner, 'data-hidden');
 *   override.set('true');
 *   override.restore();
 * }
 * ```
 *
 * ---
 * @function createHeaderAttributeOverride
 * @param {Element} element - DOM element holding the attribute.
 * @param {string} attribute - Attribute name whose value or absence must be restored.
 * @returns {HeaderDomOverride} Handle to apply a temporary value and release its ownership.
 */
export function createHeaderAttributeOverride(element: Element, attribute: string): HeaderDomOverride {
	return createOverride(
		element,
		`attribute:${attribute}`,
		() => ({ value: element.getAttribute(attribute) }),
		({ value }) => {
			if (value === null) element.removeAttribute(attribute);
			else element.setAttribute(attribute, value);
		},
	);
}
